import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// ─────────────────────────────────────────────────────────────────────────────
// Address master "lane" (origin → destination + precomputed distance) that
// doubles as the shipment-intake geocode cache. See docs/address/*.
// FE never sends `h3Index` — the server derives it from the coordinates.
// ─────────────────────────────────────────────────────────────────────────────

export interface LatLng {
  latitude: number;
  longitude: number;
}

/** Frozen client snapshot the server fetches from core at write time. */
export interface AddressClient {
  id: number;
  name: string;
}

/**
 * Lane confirmation state:
 * - `DRAFT`     — auto-created during a shipment upload dry-run, not yet
 *                 confirmed (coords are auto-resolved, may need ops review).
 * - `CONFIRMED` — committed via upload, manually edited, or created/bulk.
 */
export type AddressState = 'DRAFT' | 'CONFIRMED';

export interface Address {
  id: string;
  /** Provider/client that owns this lane. */
  clientId: number | null;
  /** Snapshot of the owning client (id + name), server-derived + frozen. */
  client: AddressClient | null;
  /** DRAFT = new/unconfirmed (from upload dry-run); CONFIRMED = reviewed/committed. */
  state: AddressState;
  originName: string;
  originAddress: string;
  originLat: number;
  originLong: number;
  originH3Index: string;
  destinationName: string;
  destinationAddress: string;
  destinationLat: number;
  destinationLong: number;
  destinationH3Index: string;
  /** Lane distance in **meters** (integer). */
  distance: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddressListParams {
  search?: string;
  page?: number;
  limit?: number;
  /** Filter lanes to these owning clients/providers. Sent as a comma-joined
   *  `clientIds` query param (BE accepts repeated + comma-joined forms). */
  clientIds?: number[];
}

export interface AddressListResponse {
  addresses: Address[];
  total: number;
  page: number;
  limit: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Places read model (CR2-D10) — the DISTINCT endpoints behind the lane book,
// UNION-ed from origins + destinations and grouped by (normalized name, H3
// res-10 cell). The Route Planner picks stops from these; a place carries a
// representative `{ addressId, endpoint }` so a picked stop can point back at a
// real lane row.
// ─────────────────────────────────────────────────────────────────────────────

export interface Place {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  /** How many lanes touch this endpoint. */
  laneCount: number;
  /** CONFIRMED wins over DRAFT when the same place appears in both. */
  state: AddressState;
  /** Representative lane row + which side of it this place is. */
  addressId: string;
  endpoint: 'origin' | 'destination';
}

export interface PlaceListParams {
  /** Filter to these owning clients; omit for "semua client". */
  clientIds?: number[];
  search?: string;
  limit?: number;
}

/** Create/update body shape. `clientId` = owning provider/client. Coords as {latitude,longitude}. */
export interface AddressPayload {
  clientId: number;
  originName: string;
  originAddress: string;
  origin: LatLng;
  destinationName: string;
  destinationAddress: string;
  destination: LatLng;
  /** meters (integer), ≥ 0. */
  distance: number;
}

/**
 * A single lane inside a bulk upsert — same as create body minus `clientId`,
 * which is sent once at the top level of the bulk request (see `bulkUpsert`).
 */
export type AddressLanePayload = Omit<AddressPayload, 'clientId'>;

/** Partial update — send only changed fields. */
export type UpdateAddressPayload = Partial<AddressPayload>;

export interface BulkUpsertResult {
  total: number;
  created: number;
  updated: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV import (dry-run + commit) — server-side validate/upsert. Body is
// multipart/form-data with a single `file` part. See §7 of
// docs/address/address-api-fe.md. Lanes are deduped/upserted by name pair.
// ─────────────────────────────────────────────────────────────────────────────

export interface AddressImportError {
  rowNumber: number;
  field: string;
  value: string;
  message: string;
}

export interface AddressImportSummary {
  totalRows: number;
  toCreate: number; // new name-pair lanes
  toUpdate: number; // existing lanes to upsert
  errors: number;
  ignoredColumns: string[];
}

export interface AddressImportPreviewResult {
  summary: AddressImportSummary;
  errors: AddressImportError[];
}

export interface AddressImportCommitResult {
  totalRows: number;
  created: number;
  updated: number;
}

export type AddressImportErrorCode =
  | 'IMPORT_FILE_REQUIRED'
  | 'IMPORT_FILE_TOO_LARGE'
  | 'IMPORT_FILE_INVALID'
  | 'IMPORT_TOO_MANY_ROWS'
  | 'IMPORT_TOO_MANY_ERRORS'
  | 'IMPORT_VALIDATION_FAILED'
  | 'UNKNOWN';

export class AddressImportException extends Error {
  code: AddressImportErrorCode;
  preview?: AddressImportPreviewResult;
  constructor(
    code: AddressImportErrorCode,
    message: string,
    preview?: AddressImportPreviewResult
  ) {
    super(message);
    this.code = code;
    this.preview = preview;
  }
}

// Canonical import columns. Nama + alamat + koordinat wajib; Distance opsional
// (meter, default 0). Unknown columns are ignored (reported in ignoredColumns).
export const ADDRESS_IMPORT_TEMPLATE_HEADERS = [
  'Origin Name',
  'Origin Address',
  'Origin Lat',
  'Origin Long',
  'Destination Name',
  'Destination Address',
  'Destination Lat',
  'Destination Long',
  'Distance',
] as const;

export const ADDRESS_IMPORT_TEMPLATE_FILENAME = 'address-import-template.csv';

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

// Normalizes a failed import request into an AddressImportException, carrying
// the preview payload the backend echoes on 409 IMPORT_VALIDATION_FAILED.
const parseAddressImportError = (error: any): AddressImportException => {
  const data = error?.response?.data;
  const code = (data?.error as AddressImportErrorCode) || 'UNKNOWN';
  const message =
    (data?.message as string) ||
    (typeof data?.error === 'string' ? data.error : null) ||
    getBackendErrorMessage(error);
  const preview =
    data && typeof data === 'object' && data.data && data.data.summary
      ? (data.data as AddressImportPreviewResult)
      : undefined;
  return new AddressImportException(code, message, preview);
};

export const addressesAPI = {
  /** Paginated + searchable lane list (newest first). */
  list: async (
    params: AddressListParams = {}
  ): Promise<AddressListResponse> => {
    try {
      // Serialize `clientIds` as a single comma-joined string (BE accepts
      // repeated + comma-joined forms) — mirrors invoices.export.
      const { clientIds, ...rest } = params;
      const query: Record<string, unknown> = { ...rest };
      if (clientIds && clientIds.length > 0) {
        query.clientIds = clientIds.join(',');
      }
      const response = await Axios.get('/v1/addresses', { params: query });
      return unwrap<AddressListResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  get: async (id: string): Promise<Address> => {
    try {
      const response = await Axios.get(`/v1/addresses/${id}`);
      return unwrap<Address>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Distinct lane endpoints for the Route Planner place picker (CR2-D10).
   * `clientIds` serialized comma-joined like `list`; empty result is normal for
   * a client whose lanes bypass the address book (TODO-32).
   */
  places: async (params: PlaceListParams = {}): Promise<Place[]> => {
    try {
      const { clientIds, ...rest } = params;
      const query: Record<string, unknown> = { ...rest };
      if (clientIds && clientIds.length > 0) {
        query.clientIds = clientIds.join(',');
      }
      const response = await Axios.get('/v1/addresses/places', {
        params: query,
      });
      const body = unwrap<{ places?: Place[]; data?: Place[] } | Place[]>(
        response?.data
      );
      // Contract: a plain array. Tolerate the two wrapper spellings seen so far.
      return Array.isArray(body) ? body : (body?.places ?? body?.data ?? []);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** 409 DUPLICATE_LANE / DUPLICATE_CODE, 400 INVALID_DISTANCE on conflict. */
  create: async (payload: AddressPayload): Promise<Address> => {
    try {
      const response = await Axios.post('/v1/addresses', payload);
      return unwrap<Address>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  update: async (
    id: string,
    payload: UpdateAddressPayload
  ): Promise<Address> => {
    try {
      const response = await Axios.patch(`/v1/addresses/${id}`, payload);
      return unwrap<Address>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** Hard delete → 204 No Content. */
  remove: async (id: string): Promise<void> => {
    try {
      await Axios.delete(`/v1/addresses/${id}`);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Upsert many lanes for one client by origin+destination name pair (one
   * transaction). `clientId` applies to the whole batch (top-level, not
   * per-row); ADMIN must send it. 400 CLIENT_ID_REQUIRED / EMPTY_PAYLOAD.
   */
  bulkUpsert: async (
    clientId: number,
    addresses: AddressLanePayload[]
  ): Promise<BulkUpsertResult> => {
    try {
      const response = await Axios.post('/v1/addresses/bulk', {
        clientId,
        addresses,
      });
      return unwrap<BulkUpsertResult>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** CSV import preview — validate + classify rows, no DB write. `clientId`
   *  scopes the imported lanes to their owning provider/client (like shipment
   *  import). */
  importDryRun: async (params: {
    clientId: number;
    file: File;
  }): Promise<AddressImportPreviewResult> => {
    try {
      const form = new FormData();
      form.append('clientId', String(params.clientId));
      form.append('file', params.file, params.file.name);
      const response = await Axios.post('/v1/addresses/import:dry-run', form, {
        headers: { 'Content-Type': undefined },
      });
      return unwrap<AddressImportPreviewResult>(response.data);
    } catch (error) {
      throw parseAddressImportError(error);
    }
  },

  /** CSV import commit — upsert by name pair for `clientId`. 409
   *  IMPORT_VALIDATION_FAILED if any row error (the thrown exception carries
   *  the preview). */
  importCommit: async (params: {
    clientId: number;
    file: File;
  }): Promise<AddressImportCommitResult> => {
    try {
      const form = new FormData();
      form.append('clientId', String(params.clientId));
      form.append('file', params.file, params.file.name);
      const response = await Axios.post('/v1/addresses/import:commit', form, {
        headers: { 'Content-Type': undefined },
      });
      return unwrap<AddressImportCommitResult>(response.data);
    } catch (error) {
      throw parseAddressImportError(error);
    }
  },
};
